import { useAuth } from '@/app/_auth/auth-hooks'
import { AuthStep } from '@/app/_auth/auth-step-navigator'
import { AuthStepTemplate } from '@/app/_auth/steps/_common/auth-step-template'
import { useRegistrationForm } from '@/app/_auth/steps/_common/registration-form-context'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import type { ConsentSettings } from '@/hooks/api/_types'
import { useRegistrationMutation } from '@/hooks/api/use-registration-mutation'
import { useQueryClient } from '@tanstack/react-query'
import { T, useTranslate } from '@tolgee/react'
import { Field, Form } from 'react-final-form'

export function RegisterConsentStep() {
  const { t } = useTranslate()
  const { setStep } = useAuth()
  const queryClient = useQueryClient()
  const { orgValues, purposeValues, consentValues, setConsentValues } = useRegistrationForm()
  const { isPending, mutate } = useRegistrationMutation({
    onSuccess: () => {
      // Remove cached queries (including error state) so fresh data is fetched after registration
      queryClient.removeQueries({ queryKey: ['get-emergence-user'] })
      queryClient.removeQueries({ queryKey: ['domains'] })
      setStep(AuthStep.GetSessionData)
    },
    onError: (e) => {
      console.error(e)
      toast.error('Failed to submit registration')
    },
  })

  const onInvalid = (nextStep: AuthStep) => {
    toast.error(t('auth.steps.preferences.errorToast'))
    setStep(nextStep)
  }

  const submit = (values: ConsentSettings) => {
    setConsentValues(values)

    if (!orgValues) return onInvalid(AuthStep.RegisterOrg)
    if (!purposeValues) return onInvalid(AuthStep.RegisterPurpose)

    mutate({ ...values, ...orgValues, ...purposeValues })
  }

  const back = (values: ConsentSettings) => {
    setConsentValues(values)
    setStep(AuthStep.RegisterPurpose)
  }

  return (
    <AuthStepTemplate>
      <Form<ConsentSettings>
        onSubmit={submit}
        initialValues={consentValues ?? { allowMarketing: true, allowData: true }}
      >
        {({ handleSubmit, values }) => (
          <form onSubmit={handleSubmit} className='flex flex-col gap-8'>
            <AuthStepTemplate.Header>
              <AuthStepTemplate.H2 keyName='auth.steps.preferences.title' />
            </AuthStepTemplate.Header>
            <div className='flex w-full flex-col gap-3'>
              <Field name='allowData'>
                {({ input }) => (
                  <div className='flex gap-3'>
                    <Checkbox
                      autoFocus
                      className='mt-2'
                      checked={input.value}
                      onCheckedChange={input.onChange}
                      name={input.name}
                    />
                    <Label htmlFor={input.name} variant='checkbox'>
                      <T keyName='auth.steps.preferences.allowData' />
                    </Label>
                  </div>
                )}
              </Field>
              <Field name='allowMarketing'>
                {({ input }) => (
                  <div className='flex gap-3'>
                    <Checkbox
                      className='mt-2'
                      checked={input.value}
                      onCheckedChange={input.onChange}
                      name={input.name}
                    />
                    <Label htmlFor={input.name} variant='checkbox'>
                      <T keyName='auth.steps.preferences.allowFeedbackRequests' />
                    </Label>
                  </div>
                )}
              </Field>
              <div className='mx-auto max-w-xs py-5 text-center text-sm font-medium'>
                <T
                  keyName='auth.steps.preferences.terms'
                  params={{
                    t: (
                      <a
                        className='text-grey-400 underline'
                        href={import.meta.env.VITE_TERMS_OF_SERVICE_URL}
                        target='_blank'
                        title={t('navigation.privacyPolicy.a11y')}
                      />
                    ),
                    p: (
                      <a
                        className='text-grey-400 underline'
                        href={import.meta.env.VITE_PRIVACY_POLICY_URL}
                        target='_blank'
                        title={t('navigation.terms.a11y')}
                      />
                    ),
                  }}
                />
              </div>
              <AuthStepTemplate.ButtonRow>
                <Button
                  onClick={() => back(values)}
                  variant='mango-outline'
                  shape='square'
                  className='w-full'
                  size='sm'
                  disabled={isPending}
                >
                  <T keyName='auth.steps.common.buttons.back' />
                </Button>
                <Button
                  loading={isPending}
                  shape='square'
                  size='sm'
                  className='w-full'
                  type='submit'
                >
                  <T keyName='auth.steps.common.buttons.createAccount' />
                </Button>
              </AuthStepTemplate.ButtonRow>
            </div>
          </form>
        )}
      </Form>
      <AuthStepTemplate.Footer />
    </AuthStepTemplate>
  )
}
